import { LIST_ROWS_PER_PAGE } from '../constants';
import axiosInstance from './api';
import { createFirebaseUser } from './firebaseUserProvisioning';
import { dedupePanelLoginRequest } from './panelLoginCache';
import { pickNotificationCount } from '../utils/notificationCount';
import { normalizeWhatsAppCommunityLink } from '../utils/whatsappCommunityLink';
import { normalizePartnerStatus } from '../utils/partnerStatus';
import { normalizePartnerAddedFrom } from '../utils/partnerAddedFrom';
import { normalizePartnerSubscriptionPlan } from '../utils/partnerSubscriptionPlans';
import { normalizeAcceptedSubscriptionPlan } from '../utils/partnerAcceptPlans';

const getErrorMessage = (error, fallback) => error?.message || error?.error?.message || fallback;

export const normalizePartnerEmails = (user) => {
  const source = user ?? {};
  const fromArray = Array.isArray(source.emails)
    ? source.emails.map((value) => String(value ?? '').trim()).filter(Boolean)
    : [];
  const legacyEmail = source.email ? [String(source.email).trim()] : [];
  const emails = [...new Set(fromArray.length > 0 ? fromArray : legacyEmail)];

  return {
    emails,
    email: emails[0] ?? '',
  };
};

export const formatPartnerEmails = (partner) => {
  const { emails, email } = normalizePartnerEmails(partner);
  if (emails.length > 0) return emails.join(', ');
  return email || '';
};

export const getPartnerLoginEmails = (partner) => {
  const { emails } = normalizePartnerEmails(partner);
  return emails;
};

export const isSolePartnerLogin = (partner) => getPartnerLoginEmails(partner).length <= 1;

export const hasPartnerCommunity = (partner) =>
  Boolean(partner?.community && typeof partner.community === 'object');

export const getPartnerProfileId = (partner) => {
  const profileId = partner?.partnerProfileId ?? partner?.partnerProfile?.id ?? partner?.id ?? null;
  return profileId ? String(profileId).trim() : null;
};

const normalizePartnerCommunity = (community) => {
  if (!community || typeof community !== 'object') {
    return null;
  }

  return {
    channelId: community.channelId ?? null,
    name: community.name ?? '',
    description: community.description ?? '',
    imageUrl: community.imageUrl ?? null,
    type: community.type ?? null,
    accessPolicy: community.accessPolicy ?? null,
    enabled: Boolean(community.enabled),
    isPinned: Boolean(community.isPinned),
    requireJoinApproval: Boolean(community.requireJoinApproval),
    attendeeEligibility: community.attendeeEligibility ?? null,
    isArchived: Boolean(community.isArchived),
    createdAt: community.createdAt ?? null,
    updatedAt: community.updatedAt ?? null,
  };
};

export const normalizePartnerUser = (user) => {
  const source = user ?? {};
  const profile =
    source.partnerProfile && typeof source.partnerProfile === 'object' ? source.partnerProfile : {};
  const merged = { ...source, ...profile };

  const fullName = merged.fullName ?? merged.name ?? merged.displayName ?? '';
  const avatarUrl = merged.avatarUrl ?? merged.avatar ?? merged.logoUrl ?? null;
  const { emails, email } = normalizePartnerEmails(source);

  return {
    id: source.id ?? source.userId ?? source._id,
    uid: source.uid ?? source.firebaseUid ?? null,
    emails,
    email,
    fullName,
    displayName: merged.displayName ?? fullName,
    shortDescription: merged.shortDescription ?? null,
    aboutDescription: merged.aboutDescription ?? merged.description ?? null,
    description: merged.description ?? merged.aboutDescription ?? null,
    avatarUrl,
    coverBannerUrl: merged.coverBannerUrl ?? null,
    category: merged.category ?? source.category ?? '',
    status: normalizePartnerStatus(merged.status ?? source.status ?? profile.status),
    addedFrom: normalizePartnerAddedFrom(
      merged.addedFrom ??
        merged.added_from ??
        source.addedFrom ??
        source.added_from ??
        profile.addedFrom,
    ),
    socialLinks: merged.socialLinks ?? {},
    whatsappCommunityLink: normalizeWhatsAppCommunityLink(
      merged.whatsappCommunityLink ?? source.whatsappCommunityLink ?? '',
    ),
    stats: merged.stats ?? null,
    communityCreated: Boolean(merged.communityCreated ?? source.communityCreated),
    community: normalizePartnerCommunity(source.community),
    partnerProfile: Object.keys(profile).length > 0 ? profile : (source.partnerProfile ?? null),
    partnerProfileId: profile.id ?? source.partnerProfileId ?? null,
    isPartner: source.isPartner,
    temporaryDisable: Boolean(merged.temporaryDisable ?? source.temporaryDisable),
    subscriptionPlan: normalizeAcceptedSubscriptionPlan(
      source.subscriptionPlan ?? merged.subscriptionPlan ?? profile.subscriptionPlan,
    ),
    currentPlan: (() => {
      const raw = source.currentPlan ?? merged.currentPlan ?? profile.currentPlan ?? null;
      if (raw == null || typeof raw !== 'object') return null;
      return normalizePartnerSubscriptionPlan(raw);
    })(),
    createdAt: source.createdAt ?? source.created_at ?? merged.createdAt ?? null,
    updatedAt: source.updatedAt ?? source.updated_at,
    notificationCount: pickNotificationCount(source),
  };
};

/** GET /admin/partners — { data: { partners, total, limit, page } } */
export const normalizePartnersListResponse = (payload, params = {}) => {
  const inner = payload?.data;
  const root = inner ?? payload;

  const list = Array.isArray(root) ? root : (root?.partners ?? root?.users ?? []);

  const metaSource = Array.isArray(root) ? payload : root;

  const partners = list
    .filter(Boolean)
    .map(normalizePartnerUser)
    .filter((p) => p.id || p.email || p.emails?.length > 0);

  return {
    partners,
    total: Number(metaSource?.total ?? metaSource?.count ?? partners.length),
    limit: Number(metaSource?.limit ?? params.limit ?? LIST_ROWS_PER_PAGE),
    page: Number(metaSource?.page ?? params.page ?? 1),
  };
};

/** Session profile from POST /admin/partner-login `data` (user + stats, plan and counters). */
export const mapPartnerSession = (session) => {
  const { user } = session;

  return {
    id: user.id,
    uid: user.uid,
    email: user.email ?? '',
    isPartner: user.isPartner,
    partnerProfileId: user.partnerProfileId ?? null,
    fullName: user.fullName ?? '',
    shortDescription: user.shortDescription ?? null,
    description: user.description ?? null,
    category: user.category ?? null,
    status: normalizePartnerStatus(user.status),
    addedFrom: normalizePartnerAddedFrom(user.addedFrom),
    avatarUrl: user.avatarUrl ?? null,
    coverBannerUrl: user.coverBannerUrl ?? null,
    socialLinks: user.socialLinks ?? {},
    whatsappCommunityLink: normalizeWhatsAppCommunityLink(user.whatsappCommunityLink ?? ''),
    temporaryDisable: Boolean(user.temporaryDisable),
    createdAt: user.createdAt ?? null,
    updatedAt: user.updatedAt ?? null,
    stats: session.stats ?? null,
    communityCreated: Boolean(session.communityCreated),
    currentPlan: session.currentPlan ? normalizePartnerSubscriptionPlan(session.currentPlan) : null,
    notificationCount: pickNotificationCount(session),
  };
};

const requestPartnerPanelLogin = async () => {
  try {
    const response = await axiosInstance.post('/admin/partner-login');
    const payload = response.data;
    const session = payload?.data ?? {};

    if (!session.user?.isPartner) {
      return {
        success: false,
        message: payload?.message || 'Access denied. Partner privileges required.',
      };
    }

    return {
      success: true,
      data: mapPartnerSession(session),
      session,
    };
  } catch (error) {
    return {
      success: false,
      message: getErrorMessage(error, 'Failed to verify partner access'),
      error,
    };
  }
};

/** POST /admin/partner-login — after Firebase sign-in; 403 if not partner */
export const postPartnerPanelLogin = (options = {}) =>
  dedupePanelLoginRequest('partner', requestPartnerPanelLogin, options);

export const listPartners = async ({ limit = LIST_ROWS_PER_PAGE, page = 1, status } = {}) => {
  try {
    const params = { limit, page };
    if (status) {
      params.status = status;
    }

    const response = await axiosInstance.get('/admin/partners', {
      params,
    });
    return {
      success: true,
      data: response.data,
    };
  } catch (error) {
    return {
      success: false,
      message: getErrorMessage(error, 'Failed to fetch partners'),
      error,
    };
  }
};

const grantPartner = async ({ email, fullName, uid, partnerId }) => {
  const body = partnerId
    ? {
        partnerId: String(partnerId).trim(),
        email: email?.trim(),
        uid: uid?.trim(),
      }
    : {
        email: email?.trim(),
        fullName: fullName?.trim(),
        uid: uid?.trim(),
      };

  try {
    const response = await axiosInstance.post('/admin/partners', body);
    const payload = response.data;

    return {
      success: true,
      data: payload?.data?.partner ?? payload?.data?.user ?? payload?.data ?? payload,
      message: payload?.message || 'Partner access granted',
      statusCode: response.status,
    };
  } catch (error) {
    return {
      success: false,
      message: getErrorMessage(error, 'Failed to add partner'),
      error,
    };
  }
};

/**
 * Grant partner access via POST /admin/partners.
 * Resolves Firebase uid from email/password (sign-in first, then create), then sends `uid`.
 * New partner: `{ email, fullName, uid }` — existing partner: `{ partnerId, email, uid }`.
 */
export const provisionAndGrantPartner = async ({ email, password, fullName, partnerId }) => {
  const firebaseResult = await createFirebaseUser(email?.trim(), password);
  if (!firebaseResult.success) {
    return firebaseResult;
  }

  if (partnerId) {
    return grantPartner({
      partnerId,
      email: email?.trim(),
      uid: firebaseResult.uid,
    });
  }

  return grantPartner({
    email: email?.trim(),
    fullName: fullName?.trim(),
    uid: firebaseResult.uid,
  });
};

/**
 * PATCH /admin/partners/:id
 * - Multiple logins: `{ email }` removes one staff login
 * - Sole login: no body removes the whole partner org
 */
export const revokePartnerAccess = async ({ partnerId, email, removeWholePartner = false }) => {
  try {
    const response = removeWholePartner
      ? await axiosInstance.patch(`/admin/partners/${partnerId}`)
      : await axiosInstance.patch(`/admin/partners/${partnerId}`, {
          email: email?.trim(),
        });

    return {
      success: true,
      data: response.data,
      message:
        response.data?.message ||
        (removeWholePartner ? 'Partner access revoked' : 'Partner login removed'),
    };
  } catch (error) {
    return {
      success: false,
      message: getErrorMessage(
        error,
        removeWholePartner ? 'Failed to revoke partner access' : 'Failed to remove partner login',
      ),
      error,
    };
  }
};

/**
 * PATCH /admin/partners/:id/status
 * Body `{ status: "live" | "rejected", amount?: number }` — amount only for live (AED).
 */
export const updatePartnerStatus = async ({ partnerProfileId, status, amount }) => {
  try {
    const body = { status };
    if (status === 'live' && amount != null && amount !== '') {
      body.amount = Number(amount);
    }

    const response = await axiosInstance.patch(`/admin/partners/${partnerProfileId}/status`, body);

    return {
      success: true,
      data: response.data?.data ?? response.data,
      message: response.data?.message || 'Partner status updated',
    };
  } catch (error) {
    return {
      success: false,
      message: getErrorMessage(error, 'Failed to update partner status'),
      error,
    };
  }
};

/**
 * POST /admin/partners/:partnerId/set-password — public activation; no auth required.
 * Body `{ password: string, token: string }` — token comes from the emailed activation link.
 */
export const setPartnerPassword = async ({ partnerId, password, token }) => {
  const id = String(partnerId ?? '').trim();
  const activationToken = String(token ?? '').trim();
  if (!id || !activationToken) {
    return {
      success: false,
      message: 'Partner activation link is invalid.',
    };
  }

  try {
    const response = await axiosInstance.post(`/admin/partners/${id}/set-password`, {
      password: String(password ?? ''),
      token: activationToken,
    });

    return {
      success: true,
      data: response.data?.data ?? response.data,
      message: response.data?.message || 'Password set successfully. You can sign in now.',
    };
  } catch (error) {
    return {
      success: false,
      message: getErrorMessage(error, 'Failed to set partner password'),
      error,
    };
  }
};

/**
 * POST /admin/partners/:id/activation-email — email a fresh one-time activation link.
 */
export const resendPartnerActivationEmail = async ({ partnerProfileId }) => {
  try {
    const response = await axiosInstance.post(
      `/admin/partners/${partnerProfileId}/activation-email`,
    );

    return {
      success: true,
      data: response.data?.data ?? response.data,
      message: response.data?.message || 'Activation email sent',
    };
  } catch (error) {
    return {
      success: false,
      message: getErrorMessage(error, 'Failed to send activation email'),
      error,
    };
  }
};

/**
 * PATCH /admin/partners/:id/subscription-pricing
 * Body `{ amount: number }` — updates or creates partner portal fee (AED).
 */
export const updatePartnerSubscriptionPricing = async ({ partnerProfileId, amount }) => {
  try {
    const response = await axiosInstance.patch(
      `/admin/partners/${partnerProfileId}/subscription-pricing`,
      { amount: Number(amount) },
    );

    return {
      success: true,
      data: response.data?.data ?? response.data,
      message: response.data?.message || 'Partner subscription amount updated successfully',
    };
  } catch (error) {
    return {
      success: false,
      message: getErrorMessage(error, 'Failed to update partner subscription amount'),
      error,
    };
  }
};

/**
 * PATCH /admin/partners/:id/temporary-disable
 * Body `{ temporaryDisable }` temporarily restricts or restores partner portal access.
 */
export const setPartnerTemporaryDisable = async ({ partnerId, temporaryDisable }) => {
  try {
    const response = await axiosInstance.patch(`/admin/partners/${partnerId}/temporary-disable`, {
      temporaryDisable: Boolean(temporaryDisable),
    });

    return {
      success: true,
      data: response.data?.data ?? response.data,
      message:
        response.data?.message ||
        (temporaryDisable
          ? 'Partner organization temporarily disabled'
          : 'Partner organization enabled'),
    };
  } catch (error) {
    return {
      success: false,
      message: getErrorMessage(
        error,
        temporaryDisable
          ? 'Failed to disable partner organization'
          : 'Failed to enable partner organization',
      ),
      error,
    };
  }
};
